<script setup lang="ts">
import { computed } from 'vue'
import type { RiskReview } from '@/api/client'

const props = defineProps<{
  riskReviews: RiskReview[]
}>()

const STANCE_META: Record<string, { label: string; emoji: string; type: 'danger' | 'warning' | 'info'; desc: string }> = {
  aggressive: { label: '激进派', emoji: '🚀', type: 'danger', desc: '追求收益最大化, 容忍波动' },
  conservative: { label: '保守派', emoji: '🛡️', type: 'warning', desc: '本金安全优先, 从严设防' },
  neutral: { label: '中性派', emoji: '⚖️', type: 'info', desc: '基准视角, 平衡取舍' },
}

/** 风险分值: concerns 数量越多分越高 (0~100, 简单可解释口径) */
function riskScore(review: RiskReview): number {
  return Math.min(100, (review.concerns?.length || 0) * 20 + (review._failed ? 30 : 0))
}

function scoreType(score: number): 'success' | 'warning' | 'danger' {
  if (score >= 60) return 'danger'
  if (score >= 40) return 'warning'
  return 'success'
}

const ordered = computed(() => {
  const order = ['aggressive', 'conservative', 'neutral']
  return [...props.riskReviews].sort(
    (a, b) => order.indexOf(a.stance) - order.indexOf(b.stance),
  )
})
</script>

<template>
  <el-card shadow="never" class="risk-panel">
    <template #header>
      <span class="title">🛡️ 三方风控委员会质询 (Risk Committee)</span>
    </template>

    <el-row :gutter="12">
      <el-col v-for="review in ordered" :key="review.stance" :span="8">
        <div class="member-card" :class="`member-${review.stance}`">
          <div class="member-head">
            <span class="member-name">{{ STANCE_META[review.stance]?.emoji || '👤' }} {{ STANCE_META[review.stance]?.label || review.stance }}</span>
            <el-tag :type="STANCE_META[review.stance]?.type || 'info'" effect="dark" size="small">
              {{ STANCE_META[review.stance]?.label || review.stance }}
            </el-tag>
          </div>
          <div class="member-desc">{{ STANCE_META[review.stance]?.desc || '' }}</div>

          <el-divider style="margin: 8px 0;" />

          <p class="member-argument">{{ review.argument || '-' }}</p>

          <div v-if="review.concerns?.length" class="concerns">
            <el-tag
              v-for="(c, i) in review.concerns"
              :key="i"
              type="danger"
              effect="plain"
              size="small"
              class="concern-tag"
            >
              ⚠️ {{ c }}
            </el-tag>
          </div>

          <div class="member-footer">
            <div class="advice">
              <strong>仓位建议:</strong> {{ review.position_advice || '-' }}
            </div>
            <div class="score">
              <span class="score-label">风险分</span>
              <el-tag :type="scoreType(riskScore(review))" effect="dark" size="small">
                {{ riskScore(review) }}
              </el-tag>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
  </el-card>
</template>

<style scoped>
.risk-panel { display: flex; flex-direction: column; }
.title { font-size: 15px; font-weight: 600; }
.member-card {
  border: 1px solid var(--border-color); border-radius: 8px; padding: 12px;
  height: 100%; display: flex; flex-direction: column; gap: 6px;
  background: var(--bg-elevated);
}
.member-aggressive { border-top: 3px solid var(--danger); }
.member-conservative { border-top: 3px solid var(--warning); }
.member-neutral { border-top: 3px solid var(--accent); }
.member-head { display: flex; justify-content: space-between; align-items: center; }
.member-name { font-size: 14px; font-weight: 700; color: var(--text-primary); }
.member-desc { font-size: 11px; color: var(--text-muted); }
.member-argument { font-size: 13px; color: var(--text-primary); line-height: 1.6; margin: 0; flex: 1; }
.concerns { display: flex; flex-wrap: wrap; gap: 4px; }
.concern-tag { margin-bottom: 2px; }
.member-footer { border-top: 1px dashed var(--border-color); padding-top: 8px; }
.advice { font-size: 12px; color: var(--text-secondary); line-height: 1.5; }
.score { margin-top: 6px; display: flex; align-items: center; gap: 6px; }
.score-label { font-size: 12px; color: var(--text-secondary); }
</style>
